import { useCallback, useEffect, useMemo, useState } from 'react';
import { issuesApi } from '../../../api/issuesApi';
import { ReportedIssue } from '../../../types/issues';
import { Building } from '../../../types/protocols';
import { BaseComponentImpl } from '../../../types/implementations';
import { ImageSize, getImagePathWithSize, resolveFirebaseImageUrl } from '../../../Helpers/ImageLoader';
import { orderPhotos } from '../../../Helpers/photoOrdering';
import { ReportedIssueRow } from '../../WorkOrders/components/shared';

/** Where a reported issue links to: the issue while active, the work order it became, else nowhere. */
const hrefFor = ({ issue, state }: ReportedIssue): string | undefined => {
    if (state === 'active') return `/management?issue=${encodeURIComponent(issue.key)}`;
    if (state === 'converted' && issue.convertedToWorkOrderKey) {
        return `/management?wo=${encodeURIComponent(issue.convertedToWorkOrderKey)}`;
    }
    return undefined;
};

/**
 * Fel hittade under arbetet on the work order page (internal users): the issues
 * reported from the order, active and archived, as rows with the newest
 * photo's thumbnail (the original while no thumbnail exists yet). Loaded
 * only while `enabled` (an open order).
 */
export function useReportedIssues(
    workOrderKey: string | undefined,
    enabled: boolean,
    building: Building | null,
    componentNames: Record<string, string>
) {
    const [reported, setReported] = useState<ReportedIssue[]>([]);
    const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

    const reload = useCallback(async () => {
        if (!workOrderKey || !enabled) {
            setReported([]);
            return;
        }
        try {
            setReported(await issuesApi.getBySourceWorkOrder(workOrderKey));
        } catch (err) {
            console.warn('Could not load reported issues', err);
        }
    }, [workOrderKey, enabled]);

    useEffect(() => { void reload(); }, [reload]);

    const firstPhotoPaths = useMemo(() => {
        const paths: Record<string, string> = {};
        for (const { issue } of reported) {
            const first = orderPhotos(issue.photos || {})[0];
            if (first?.storagePath) paths[issue.key] = first.storagePath;
        }
        return paths;
    }, [reported]);
    const pathsSignature = Object.entries(firstPhotoPaths).map(([k, p]) => `${k}:${p}`).join(',');

    useEffect(() => {
        let cancelled = false;
        Promise.all(Object.entries(firstPhotoPaths).map(async ([key, path]) => {
            const thumbnailPath = getImagePathWithSize(path, ImageSize.THUMBNAIL_SMALL);
            const url = await (thumbnailPath ? resolveFirebaseImageUrl(thumbnailPath) : Promise.reject(new Error('no thumbnail')))
                .catch(() => resolveFirebaseImageUrl(path))
                .catch(() => undefined);
            return [key, url] as const;
        })).then((entries) => {
            if (cancelled) return;
            setThumbnails(Object.fromEntries(entries.filter((e): e is readonly [string, string] => !!e[1])));
        });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- the signature stands in for the paths
    }, [pathsSignature]);

    const rows: ReportedIssueRow[] = useMemo(() => reported.map((entry) => {
        const { issue } = entry;
        const raw = issue.componentKey ? building?.model.structure?.components?.[issue.componentKey] : undefined;
        const componentName = issue.componentKey
            ? (componentNames[issue.componentKey] || (raw ? new BaseComponentImpl(raw).displayName() : 'Komponent'))
            : issue.componentCategory?.label;
        return {
            key: issue.key,
            description: issue.description,
            componentName,
            thumbnailUrl: thumbnails[issue.key],
            state: entry.state,
            href: hrefFor(entry),
        };
    }), [reported, building, componentNames, thumbnails]);

    return { rows, reload };
}
